/**
 * Branding configuration loader.
 *
 * Serves BOTH multi-tenancy models:
 *
 *  - WHITELABEL (apptics, fmj) = full infra fork, per-deploy brand via
 *    `VITE_BRAND` / `VITE_APP_NAME` env vars (set in `.env.<brand>.prod`).
 *    Separate Vercel, Clerk, DB. See WHITELABEL.md.
 *
 *  - PARTNER MODE (testpartner, BNP, ...) = shared deploy, per-request
 *    brand: the Vercel middleware resolves the request host and injects that
 *    ONE partner (with its code brand file, if any) into index.html; read
 *    through `./current-partner.ts`. See PARTNER_MODE.md.
 *
 * Resolution priority for `detectBrand()`:
 *  1. VITE_BRAND env var              <- whitelabel explicit
 *  2. Injected host partner's code brand <- partner mode runtime
 *  3. VITE_APP_NAME env var (lowercased, matched against brand registry)
 *  4. 'tagadapay' default
 *
 * Field-level env overrides (VITE_APP_NAME, VITE_APP_LOGO, VITE_PRIMARY_COLOR,
 * etc.) in `getBrandingConfig()` are whitelabel-mode conveniences only and
 * are ignored in partner mode (where the hostname dictates the brand).
 */

import { brands, neutralBrand, type BrandConfig, type BrandName } from './brands';
import { currentPartner, knownPartnerBrandByOrigin } from './current-partner';

/** The partner injected for the current browser host (null on the server / main domain). */
const hostPartner = currentPartner();

/** The host partner's code brand file, when it ships one (DB-branded partners: null). */
const hostCodeBrand: BrandConfig | null = hostPartner?.brandConfig ?? null;

/**
 * Resolve a partner brand config from a full URL string (typically the
 * `redirect_url` query param on primary /sign-in). Returns null unless
 * the URL's origin exactly matches the appHost of the partner the middleware
 * injected for this sign-in bounce.
 *
 * Used by custom-sign-in.tsx to re-skin the primary's sign-in page when
 * a partner satellite bounced the user here, so the cross-origin hop
 * stays visually partner-branded.
 */
export const resolvePartnerBrand = (redirectUrl: string | null | undefined): BrandConfig | null =>
  knownPartnerBrandByOrigin(redirectUrl);

/**
 * Detect brand from environment variables or hostname.
 * Priority:
 *  1. VITE_BRAND (explicit white-label deploy)
 *  2. Injected host partner with a code brand file
 *  3. VITE_APP_NAME lowercase match
 *  4. Default 'tagadapay'
 */
function detectBrand(): BrandName {
  // 1. Explicit VITE_BRAND takes priority
  if (import.meta.env?.VITE_BRAND) {
    return import.meta.env.VITE_BRAND as BrandName;
  }

  // 2. Partner whitelabel via hostname (runtime, injected by the middleware).
  //    Only when the partner ships a code brand file; DB-branded partners fall
  //    through to the default brand and get their identity from the runtime
  //    DB overlay.
  if (hostPartner && hostCodeBrand) {
    return hostPartner.brand as BrandName;
  }

  // 3. Auto-detect from VITE_APP_NAME (case-insensitive)
  const appName = import.meta.env?.VITE_APP_NAME?.toLowerCase();
  if (appName && brands[appName]) {
    return appName as BrandName;
  }

  // 4. Default to tagadapay
  return 'tagadapay';
}

const brandName = detectBrand();

/**
 * True when the current host matched a partner that ships NO code brand file
 * (`brandSource:'db'` or a brand key absent from the registry) — so its visual
 * identity (logo / color / name) must come from the runtime DB branding
 * overlay instead of a bundled brand file. Always false in whitelabel mode
 * (explicit `VITE_BRAND`) and on the main tagada domain (no partner match).
 */
export const usesDefaultBrand =
  !import.meta.env?.VITE_BRAND && !!hostPartner && !hostCodeBrand;

/** True when the current browser host is any registered partner host (code or DB-branded). */
export const isPartnerHost = !!hostPartner;

/** True on a whitelabel deploy (explicit `VITE_BRAND`, e.g. apptics/fmj). */
export const isWhitelabel = !!import.meta.env?.VITE_BRAND;

// Base brand config: neutral for DB-branded partner hosts (so unfilled fields
// never fall back to TagadaPay), otherwise the detected code brand.
const baseBrandConfig: BrandConfig = usesDefaultBrand
  ? neutralBrand
  : (brands[brandName] ?? (hostPartner?.brand === brandName ? hostCodeBrand : null) ?? brands.tagadapay);

type EnvBrandOverrides = {
  VITE_APP_NAME?: string;
  VITE_APP_DESCRIPTION?: string;
  VITE_APP_URL?: string;
  VITE_APP_LOGO?: string;
  VITE_PRIMARY_COLOR?: string;
  VITE_FAVICON?: string;
};

/** Layer the whitelabel VITE_* field overrides onto a base brand. */
const applyEnvBrandOverrides = (base: BrandConfig, env: EnvBrandOverrides): BrandConfig => {
  const overrides: Partial<BrandConfig> = {};
  if (env.VITE_APP_NAME) overrides.appName = env.VITE_APP_NAME;
  if (env.VITE_APP_DESCRIPTION) overrides.appDescription = env.VITE_APP_DESCRIPTION;
  if (env.VITE_APP_URL) overrides.appUrl = env.VITE_APP_URL;
  if (env.VITE_APP_LOGO) overrides.logo = env.VITE_APP_LOGO;
  if (env.VITE_PRIMARY_COLOR) overrides.primaryColor = env.VITE_PRIMARY_COLOR;
  if (env.VITE_FAVICON) overrides.favicon = env.VITE_FAVICON;
  return { ...base, ...overrides };
};

/**
 * Pure branding resolution. Partner-mode hosts (code- OR DB-branded) resolve
 * purely from their hostname brand + runtime DB overlay, so the shared deploy's
 * whitelabel VITE_* overrides must NOT be applied here — the shared partner-mode
 * deploy carries the primary's `VITE_APP_NAME` ("TagadaPay"), which would
 * otherwise overwrite the `appName` of a code-branded partner that has no DB
 * `appName` (e.g. verasolution), leaking "TagadaPay" into its title and logo
 * alt. VITE_* overrides are a whitelabel convenience for the primary /
 * `VITE_BRAND` deploys only (`isPartnerHost` is always false there).
 */
export const resolveBrandingConfig = (
  base: BrandConfig,
  env: EnvBrandOverrides,
  hostIsPartner: boolean,
): BrandConfig => (hostIsPartner ? { ...base } : applyEnvBrandOverrides(base, env));

/** Get the final branding configuration for the current deploy host. */
export const getBrandingConfig = (): BrandConfig =>
  resolveBrandingConfig(baseBrandConfig, import.meta.env ?? {}, isPartnerHost);

// Export the resolved branding configuration
export const brandingConfig = getBrandingConfig();

/** TagadaPay primary CRM only — not code-brand partners nor DB-branded partner hosts. */
export const isTagadaPay = brandName === 'tagadapay' && !usesDefaultBrand;

/**
 * Whether the TagadaPay-owned activation funnel (activate-account page with
 * Stripe card setup / MoR apply) applies on this deploy.
 */
export function isActivateAccountFunnelEnabled(): boolean {
  return isTagadaPay;
}

/**
 * Get the full logo URL (absolute URL for external use)
 * Combines the app origin with the logo path if it's a relative path
 */
export const getLogoUrl = (): string => {
  const logo = brandingConfig.logo;
  if (logo.startsWith('http://') || logo.startsWith('https://')) {
    return logo;
  }
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : import.meta.env.VITE_CRM_URL || brandingConfig.appUrl || 'https://app.tagada.io';
  return `${origin}${logo.startsWith('/') ? '' : '/'}${logo}`;
};

/**
 * Demo seeding is TagadaPay-primary CRM only — not partner hosts nor
 * DB-branded partners (`brandSource:'db'`) that fall back to tagadapay config.
 */
export const isDemoSeedingEnabled = (): boolean =>
  isTagadaPay && brandingConfig.features?.demoSeeding === true;

// Re-export types for convenience
export type { BrandConfig, BrandName };
